import { useState } from "react";

import { useAddPatientDevice } from "../../../../hooks/mutations/admin/devices";
import { usePatientDeviceTypeOptions } from "../../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../../hooks/useToast";
import {
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../AdminPage.styles";
import { AdminSelectField } from "../AdminSelectField";

interface IAddDeviceModalProps {
  /** Locked when the panel is scoped to a patient, as Retool's form was. */
  patientId?: number;
  onClose: () => void;
}

/**
 * Registers a device to a patient — Retool's `modalAddDevice`.
 *
 * The identifier is only `required` here, matching Retool: the 15-digit rule
 * lives on rename alone. See `deviceIdentifier.ts` for why that wasn't
 * tightened.
 */
export function AddDeviceModal({ patientId, onClose }: IAddDeviceModalProps) {
  const { showToast } = useToast();
  const { deviceTypes, isLoading: typesLoading } =
    usePatientDeviceTypeOptions();
  const { addDevice, isAddingDevice } = useAddPatientDevice();

  const [patientIdValue, setPatientIdValue] = useState(
    patientId === undefined ? "" : String(patientId)
  );
  const [identifier, setIdentifier] = useState("");
  const [deviceTypeId, setDeviceTypeId] = useState("");
  const [submitError, setSubmitError] = useState<string>();

  const parsedPatientId = Number.parseInt(patientIdValue, 10);
  const isValid =
    Number.isFinite(parsedPatientId) &&
    parsedPatientId > 0 &&
    identifier.trim().length > 0 &&
    deviceTypeId.length > 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitError(undefined);

    try {
      await addDevice({
        deviceTypeId,
        patientId: parsedPatientId,
        identifier: identifier.trim(),
      });
      showToast("The device was added successfully.", "success");
      onClose();
    } catch {
      // EN-5094: the API answers DeviceReassignmentConfirmationRequired when
      // the identifier belongs to a patient outside the caller's wards. The
      // portal cannot complete that here — it needs the confirmation flow the
      // patient-facing screens already have — so it says so rather than
      // silently retrying with confirmReassignment.
      setSubmitError(
        "The device could not be added. If this identifier is already registered to another patient, reassign it from that patient's record."
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Add device</ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel htmlFor="device-patient-id">Patient ID</FilterLabel>
          <FilterInput
            id="device-patient-id"
            type="number"
            value={patientIdValue}
            disabled={patientId !== undefined || isAddingDevice}
            onChange={(e) => setPatientIdValue(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="device-new-identifier">Identifier</FilterLabel>
          <FilterInput
            id="device-new-identifier"
            type="text"
            value={identifier}
            disabled={isAddingDevice}
            onChange={(e) => setIdentifier(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="device-type">Device type</FilterLabel>
          <AdminSelectField
            id="device-type"
            value={deviceTypeId}
            disabled={typesLoading || isAddingDevice}
            onChange={(e) => setDeviceTypeId(e.target.value)}
          >
            <option value="">
              {typesLoading ? "Loading..." : "Select a device type"}
            </option>
            {deviceTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </AdminSelectField>
          <FieldHint>Archived device types are not listed.</FieldHint>
        </FilterGroup>

        {submitError && <FieldError>{submitError}</FieldError>}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isAddingDevice}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={!isValid || isAddingDevice}
            onClick={handleSubmit}
          >
            {isAddingDevice ? "Saving..." : "Save"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
